import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent } from 'src/app/core/base.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { BookingDuration } from '../../models/booking-duration';
import { BookingDurationService } from '../../services/booking-duration.service';

@Component({
    selector: 'booking-settings',
    templateUrl: './booking-settings.component.html',
    styleUrls: ['./booking-settings.component.scss'],
    standalone: false
})
export class BookingSettingsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() isActive: boolean = false;
  bookingDurations: BookingDuration[] = [];
  dataSource = new MatTableDataSource<BookingDuration>([]);
  displayedColumns: string[] = ['duration', 'enabled', 'actions'];
  bookingDurationErrorMessage: string = '';
  useHours: boolean = false;

  durationForm = new FormGroup({
    duration: new FormControl<number | null>(null, [Validators.required])
  });

  constructor(
    sessionService: SessionService,
    stringHelpers: StringHelpers,
    private dialog: MatDialog,
    private bookingDurationService: BookingDurationService,
  ) {
    super(sessionService, stringHelpers);
  }

  ngOnInit(): void {
    if (this.isActive) {
      this.loadBookingDurations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive'] && changes['isActive'].currentValue === true) {
      this.loadBookingDurations();
    }
  }

  private loadBookingDurations(): void {
    this.isLoading = true;
    this.bookingDurationService.getAllByOrganization({ organizationId: this.sessionService.getUser().organizationId }).subscribe({
      next: (response) => {
        this.bookingDurations = response;
        this.dataSource = new MatTableDataSource<BookingDuration>(this.bookingDurations);
        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      }
    });
  }

  onInputModeChange(): void {
    this.durationForm.get('duration').setValue(null);
    this.bookingDurationErrorMessage = '';
  }

  addBookingDuration(): void {
    if (!this.durationForm.valid) {
      this.bookingDurationErrorMessage = 'Please enter a valid duration.';
      return;
    }

    const value = this.durationForm.get('duration').value;
    let durationInMinutes: number;

    if (this.useHours) {
      if (value < 0.5) {
        this.bookingDurationErrorMessage = 'Duration must be at least 0.5 hours.';
        return;
      }
      if (!Number.isInteger(value * 2)) {
        this.bookingDurationErrorMessage = 'Duration must be in 0.5-hour increments (e.g., 0.5, 1, 1.5, 2).';
        return;
      }
      durationInMinutes = Math.round(value * 60);
    } else {
      if (value < 30) {
        this.bookingDurationErrorMessage = 'Duration must be at least 30 minutes.';
        return;
      }
      if (value % 30 !== 0) {
        this.bookingDurationErrorMessage = 'Duration must be in 30-minute increments.';
        return;
      }
      durationInMinutes = value;
    }

    if (this.bookingDurations.find(d => d.durationMinutes === durationInMinutes)) {
      this.bookingDurationErrorMessage = 'This duration already exists.';
      return;
    }

    this.isLoading = true;
    const createData = {
      organizationId: this.sessionService.getUser().organizationId,
      durationMinutes: durationInMinutes,
      isEnabled: true,
      createdBy: this.sessionService.getUser().id,
      lastEditedBy: this.sessionService.getUser().id
    };

    this.bookingDurationService.create(createData).subscribe({
      next: (response) => {
        this.bookingDurations.push(response);
        this.bookingDurations.sort((a, b) => a.durationMinutes - b.durationMinutes);
        this.dataSource.data = this.bookingDurations;
        this.durationForm.reset();
        this.bookingDurationErrorMessage = '';
      },
      error: (error) => {
        console.error(error);
        this.bookingDurationErrorMessage = error?.error?.message || 'Failed to add duration. Please try again.';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  addStandardDurations(): void {
    const standardDurations = [30, 60, 90, 120, 150, 180, 210, 240];

    const data = standardDurations.map(duration => ({
      organizationId: this.sessionService.getUser().organizationId,
      durationMinutes: duration,
      isEnabled: true,
      createdBy: this.sessionService.getUser().id,
      lastEditedBy: this.sessionService.getUser().id
    }));

    this.isLoading = true;
    this.bookingDurationService.createStandardDurations(data).subscribe({
      next: (response) => {
        this.loadBookingDurations();
        const dialogRef = this.dialog.open(AlertModalComponent, {
          width: '35vw'
        });
        dialogRef.componentInstance.title = "Standard Durations Added";
        dialogRef.componentInstance.body = "Standard durations have been added successfully.";
        dialogRef.componentInstance.submitText = "OK";
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
        this.bookingDurationErrorMessage = error?.error?.message || 'Failed to add standard durations. Please try again.';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  toggleEnabled(duration: BookingDuration): void {
    if (!duration.id) return;
    this.isLoading = true;
    this.bookingDurationService.update(duration.id, {
      isEnabled: duration.isEnabled,
      lastEditedBy: this.sessionService.getUser().id
    }).subscribe({
      next: (response) => {
        const index = this.bookingDurations.findIndex(d => d.id === duration.id);
        if (index >= 0) {
          this.bookingDurations[index] = { ...this.bookingDurations[index], ...response };
          this.dataSource.data = [...this.bookingDurations];
        }
      },
      error: (error) => {
        console.error(error);
        duration.isEnabled = !duration.isEnabled; // Revert on error
        this.bookingDurationErrorMessage = error?.error?.message || 'Failed to update duration. Please try again.';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  removeBookingDuration(duration: BookingDuration): void {
    this.isLoading = true;
    this.bookingDurationService.delete({
      id: duration.id,
      lastEditedBy: this.sessionService.getUser().id
    }).subscribe({
      next: () => {
        this.loadBookingDurations();
      },
      error: (error) => {
        console.error(error);
        this.bookingDurationErrorMessage = error?.error?.message || 'Failed to remove duration. Please try again.';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${hours}h ${remainingMinutes}min`;
  }
}
